import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { CreateSubSession, SubscriptionEnum } from '../../common/general-types/subscription.dto';
import { CurrentUserService } from '../../common/current-user/current-user.service';
import { FirebaseService } from '../../common/firebase/firebase.service';
import { CreateSubSessionMultiProduct, PeriodEnum } from '../../common/general-types/subscription.dto';
import { rememberPendingSubscription } from '../analytics/customer-analytics';
import { PosthogService } from '../analytics/posthog.service';

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {

  public subscription = signal<SubscriptionEnum | undefined>(undefined);
  public currencyCode = signal<string | undefined>(undefined);
  public successUrl = signal<string | undefined>(undefined);

  /** Selected Subscription Plan */
  selectedPlan = signal<string>('essential');
  /** Selected Subscription Plan END */

  /** Selected Bolt-ons */
  selectedBoltOns = signal<Set<string>>(new Set());
  /** Selected Bolt-ons END */

  /** Services */
  private currentUserService = inject(CurrentUserService);
  private http = inject(HttpClient);
  private firebaseService = inject(FirebaseService);
  private posthogService = inject(PosthogService);
  /** Services - END */


  setSubscriptionLevel(sub: 'brand' | 'agency' | 'essentials' | 'pro', currencyCode: 'gbp' | 'usd') {
    console.log('🎃🎃🎃 Set Subscription Level: ', sub);
    switch (sub) {
      case 'brand':
        this.subscription.set(SubscriptionEnum.Brand);
        break;
      case 'agency':
        this.subscription.set(SubscriptionEnum.Agency);
        break;
      case 'essentials':
        this.subscription.set(SubscriptionEnum.Essentials);
        break;
      case 'pro':
        this.subscription.set(SubscriptionEnum.Pro);
        break;
    }
    this.currencyCode.set(currencyCode);
  }


  signUpSubscription() {
    console.log('--------------------------------')
    console.log('👉 signUpSubscription');
    console.log('👉 stripeCustomerId: ', this.currentUserService.focussedBrand?.stripeCustomerId || 'No stripeCustomerId');
    console.log('--------------------------------')

    if (this.currentUserService.focussedBrand && this.currentUserService.focussedBrand.stripeCustomerId) {

      this.findSubscriptionForCustomer(this.currentUserService.focussedBrand.stripeCustomerId).subscribe(subscription => {
        console.log('--------------------------------')
        console.log('👉 Existing Subscription: ', subscription);
        console.log('--------------------------------')

        if (subscription?.status == 'active') {
          console.log('👉 Subscription is active');
        } else if (subscription?.status == 'trialing') {
          console.log('👉 Subscription is trialing');
        } else if (subscription?.status == 'canceled') {
          console.log('👉 Subscription is canceled');
        } else if (subscription?.status == 'past_due') {
          console.log('👉 Subscription is past due');
        } else if (subscription?.status == 'inactive') {
          console.log('👉 Subscription is inactive');
        } else {
          this.completeSubscription();
        }
      })

    } else {
      if (this.currentUserService.focussedAgency()) {
        console.log('👉 Switch to Agency: ', this.currentUserService.focussedAgency());
      }
    }
  }

  findSubscriptionForCustomer(stripeCustomerId: string) {
    const customr_subscription_url = `${environment.shout_api.url}/subscriptions/customer/${stripeCustomerId}`;
    return this.http.get<any>(customr_subscription_url, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  completeSubscription() {
    console.log('👉 Complete Subscription');

    let cancelUrl;

    cancelUrl = `${window.location.protocol}//${window.location.host}/my-brand`;

    const request: CreateSubSessionMultiProduct = {
      stripeCustomerId: this.subscription() == SubscriptionEnum.Agency ? this.currentUserService.focussedAgency?.stripeCustomerId : this.currentUserService.focussedBrand?.stripeCustomerId,
      products: [
        {
          productId: environment.stripe.shaas21products.sub_essential,
          quantity: 1,
        },
        ...Array.from(this.selectedBoltOns()).map(boltOn => ({
          productId: boltOn,
          quantity: 1,
        }))
      ],
      successUrl: this.successUrl() ? this.successUrl()! : '',
      cancelUrl,
    };

    console.log('👉 Subscription Request: ', request);

    this.createSubscriptionLink(request).subscribe(createSubscriptionLink => {
      console.log('👉 createSubscriptionLink', createSubscriptionLink);
      this.posthogService.posthog.capture('subscription_checkout_started', {
        stripe_customer_id: request.stripeCustomerId,
        bolt_on_count: this.selectedBoltOns().size,
        currency_code: this.currencyCode(),
      });
      rememberPendingSubscription(request.successUrl);
      window.location.href = createSubscriptionLink.url;
    });
  }

  createSubscriptionLink(createSubscriptionLink: CreateSubSessionMultiProduct) {
    const create_sub_link = `${environment.shout_api.url}/subscriptions/create/multi-product`;
    return this.http.post<any>(create_sub_link, createSubscriptionLink, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  addBoltOnLineItems(createSubscriptionLink: CreateSubSessionMultiProduct) {
    const add_line_items_url = `${environment.shout_api.url}/subscriptions/add/bolt-on/line-items`;
    return this.http.post<any>(add_line_items_url, createSubscriptionLink, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  removeBoltOnLineItem(customerId: string, itemId: string) {
    const url = `${environment.shout_api.url}/subscriptions/customer/${customerId}/remove/bolt-on/${itemId}`;
    return this.http.post<any>(url, {}, {
      headers: { 'Content-Type': 'application/json' }
    });
  }

  addExtraBrandSeat(customerId: string) {
    const url = `${environment.shout_api.url}/subscriptions/customer/${customerId}/add-brand`;
    return this.http.post<unknown>(url, {}, {
      headers: { 'Content-Type': 'application/json' },
    });
  }

  getSubscriptionForCustomer(customerId: string) {
    const url = `${environment.shout_api.url}/subscriptions/customer/${customerId}`;
    return this.http.get<any>(url, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  sendApprovalRequest(
    customerId: string,
    request: {
      email: string;
      senderName?: string;
      message?: string;
      amount?: string;
      companyName?: string;
      daysUntilDue?: number;
    }
  ) {
    const send_approval_request_url = `${environment.shout_api.url}/subscriptions/customer/${customerId}/send-approval`;
    return this.http.post<any>(send_approval_request_url, request, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }

  sendSupportRequest(to: string, subject: string, body: string) {
    const request: {
      to: string;
      from: string;
      subject: string;
      bodyText: string;
    } = {
      to,
      from: 'hello@shoutugc.com',
      subject,
      bodyText: body,
    };
    const send_support_request_url = `${environment.shout_api.url}/subscriptions/customer-support/send-email`;
    return this.http.post<any>(send_support_request_url, request, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
  }
}
